import { useEffect, useMemo, useState } from 'react';
import { supplierFormInitialValues } from '../data/supplierConstants';
import {
  createSupplier,
  fetchSuppliers,
  updateSupplier,
} from '../services/supplierService';

const SUPPLIER_ACTIVE_VIEW_STORAGE_KEY = 'seaweeds.supplierActiveView';

function useSuppliers() {
  const [activeView, setActiveView] = useState(getInitialSupplierActiveView);
  const [filterValues, setFilterValues] = useState({
    supplierName: '',
    status: 'All',
  });
  const [formValues, setFormValues] = useState(supplierFormInitialValues);
  const [formErrors, setFormErrors] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [editValues, setEditValues] = useState(supplierFormInitialValues);
  const [editErrors, setEditErrors] = useState({});
  const [editError, setEditError] = useState('');
  const [isEditSaving, setIsEditSaving] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(SUPPLIER_ACTIVE_VIEW_STORAGE_KEY, activeView);
  }, [activeView]);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage('');
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [successMessage]);

  const filteredSuppliers = useMemo(() => {
    const normalizedSearch = filterValues.supplierName.trim().toLowerCase();

    return suppliers
      .filter((supplier) => {
        const matchesSearch =
          !normalizedSearch ||
          supplier.companyName.toLowerCase().includes(normalizedSearch) ||
          String(supplier.sacksMark || '').toLowerCase().includes(normalizedSearch);

        const matchesStatus =
          filterValues.status === 'All' || supplier.status === filterValues.status;

        return matchesSearch && matchesStatus;
      })
      .sort((firstSupplier, secondSupplier) =>
        String(firstSupplier.companyName || '').localeCompare(
          String(secondSupplier.companyName || ''),
          undefined,
          { sensitivity: 'base' }
        )
      );
  }, [filterValues, suppliers]);

  useEffect(() => {
    let isMounted = true;

    async function loadSuppliers() {
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchSuppliers();

        if (isMounted) {
          setSuppliers(response);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSuppliers();

    return () => {
      isMounted = false;
    };
  }, []);

  function handleFormChange(event) {
    const { name, value, files } = event.target;

    if (files?.[0]) {
      handleFileChange(files[0], setFormValues, setFormErrors, setSubmitError);
      return;
    }

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
      ...(name === 'companyName' ? { sacksMark: getInitialLetters(value) } : {}),
    }));

    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
      ...(name === 'companyName' ? { sacksMark: '' } : {}),
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;

    setFilterValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
  }

  function handleFilterReset() {
    const resetValues = {
      supplierName: '',
      status: 'All',
    };

    setFilterValues(resetValues);
  }

  function clearFormPhoto() {
    setFormValues((currentValues) => ({
      ...currentValues,
      photoUrl: '',
    }));
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      photoUrl: '',
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateSupplierForm(formValues);

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const createdSupplier = await createSupplier({
        companyName: formValues.companyName.trim(),
        sacksMark: formValues.sacksMark.trim(),
        address: formValues.address.trim(),
        contactNumber: formValues.contactNumber.trim(),
        photoUrl: formValues.photoUrl,
        status: formValues.status,
      });

      setSuppliers((currentSuppliers) => [...currentSuppliers, createdSupplier]);
      resetForm();
      setActiveView('list');
      setSuccessMessage('Supplier added successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function openEditModal(supplier) {
    setEditingSupplier(supplier);
    setEditValues({
      companyName: supplier.companyName,
      sacksMark: supplier.sacksMark || '',
      contactNumber: supplier.contactNumber || '',
      address: supplier.address,
      photoUrl: supplier.photoUrl || '',
      status: supplier.status,
    });
    setEditErrors({});
    setEditError('');
    setSuccessMessage('');
  }

  function closeEditModal() {
    setEditingSupplier(null);
    setEditValues(supplierFormInitialValues);
    setEditErrors({});
    setEditError('');
  }

  function handleEditChange(event) {
    const { name, value, files } = event.target;

    if (files?.[0]) {
      handleFileChange(files[0], setEditValues, setEditErrors, setEditError);
      return;
    }

    setEditValues((currentValues) => ({
      ...currentValues,
      [name]: value,
      ...(name === 'companyName' ? { sacksMark: getInitialLetters(value) } : {}),
    }));

    setEditErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
      ...(name === 'companyName' ? { sacksMark: '' } : {}),
    }));
    setEditError('');
    setSuccessMessage('');
  }

  function clearEditPhoto() {
    setEditValues((currentValues) => ({
      ...currentValues,
      photoUrl: '',
    }));
    setEditErrors((currentErrors) => ({
      ...currentErrors,
      photoUrl: '',
    }));
    setEditError('');
    setSuccessMessage('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();

    if (!editingSupplier) {
      return false;
    }

    const nextErrors = validateSupplierForm(editValues);

    if (Object.keys(nextErrors).length) {
      setEditErrors(nextErrors);
      return false;
    }

    setIsEditSaving(true);
    setEditError('');

    try {
      const updatedSupplier = await updateSupplier(editingSupplier.id, {
        companyName: editValues.companyName.trim(),
        sacksMark: editValues.sacksMark.trim(),
        address: editValues.address.trim(),
        contactNumber: editValues.contactNumber.trim(),
        photoUrl: editValues.photoUrl,
        status: editValues.status,
      });

      setSuppliers((currentSuppliers) =>
        currentSuppliers.map((supplier) =>
          supplier.id === editingSupplier.id ? updatedSupplier : supplier
        )
      );
      closeEditModal();
      setSuccessMessage('Supplier updated successfully.');
      return true;
    } catch (error) {
      setEditError(error.message);
      return false;
    } finally {
      setIsEditSaving(false);
    }
  }

  function resetForm() {
    setFormValues(supplierFormInitialValues);
    setFormErrors({});
    setSubmitError('');
  }

  function handleCancel() {
    resetForm();
    setActiveView('list');
  }

  return {
    activeView,
    filterValues,
    formValues,
    formErrors,
    filteredSuppliers,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    editingSupplier,
    editValues,
    editErrors,
    editError,
    isEditSaving,
    setActiveView,
    clearFormPhoto,
    handleFormChange,
    handleFilterChange,
    handleFilterSubmit,
    handleFilterReset,
    handleSubmit,
    handleCancel,
    openEditModal,
    closeEditModal,
    clearEditPhoto,
    handleEditChange,
    handleEditSubmit,
  };
}

function getInitialSupplierActiveView() {
  const storedValue = window.localStorage.getItem(SUPPLIER_ACTIVE_VIEW_STORAGE_KEY);

  if (storedValue === 'list' || storedValue === 'form') {
    return storedValue;
  }

  return 'list';
}

function handleFileChange(file, setValues, setErrors, setErrorMessage) {
  const reader = new FileReader();

  reader.onload = () => {
    setValues((currentValues) => ({
      ...currentValues,
      photoUrl: typeof reader.result === 'string' ? reader.result : '',
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      photoUrl: '',
    }));
    setErrorMessage('');
  };

  reader.onerror = () => {
    setErrorMessage('Unable to read the selected photo right now.');
  };

  reader.readAsDataURL(file);
}

function validateSupplierForm(values) {
  const errors = {};

  if (!values.companyName.trim()) {
    errors.companyName = 'Company name is required.';
  }

  if (!values.address.trim()) {
    errors.address = 'Address is required.';
  }

  if (!values.status.trim()) {
    errors.status = 'Status is required.';
  }

  return errors;
}

function getInitialLetters(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .map((word) => word.match(/[A-Za-z0-9]/)?.[0] || '')
    .join('')
    .toUpperCase();
}

export default useSuppliers;
